import Joi from 'joi';

import type { ICreateLeadRequest } from 'src/services/api';
import { type ParsedCsv } from 'src/utils/csv';
import { isPossiblePhoneNumber } from 'libphonenumber-js';

export const importLeadsFields = [
    'name',
    'email',
    'phone_number',
    'ingestion_source',
    'property',
];

const NewLeadSchema = Joi.object<ICreateLeadRequest>({
    name: Joi.string().trim().required().max(100).messages({
        'string.max': '100 characters limit exceeded',
    }),
    email: Joi.string().trim().email({ tlds: false }),
    phone_number: Joi.string().trim().custom((value, helper) => {
        const checkPhone = (number: string) => {
            return isPossiblePhoneNumber(number, 'US');
        };
        if (!checkPhone(value)) {
            return helper.error('any.invalid');
        }
        return value;
    }),
    ingestion_source: Joi.string().trim().required(),
    property: Joi.string().trim().allow(''),
}).unknown(true);

export type LeadCsvRow = {
    [K in keyof ICreateLeadRequest]: string;
};

function parseLead(row: LeadCsvRow) {
    // Normalize CSV headers, and remove any empty headers.
    const normalizedRow = Object.keys(row).reduce((acc, key) => {
        const trimmedKey = key.trim();
        if (!trimmedKey) {
            return acc;
        }
        acc[trimmedKey as keyof LeadCsvRow] = row[key as keyof LeadCsvRow];
        return acc;
    }, {} as Partial<LeadCsvRow>);

    const validationResult = NewLeadSchema.validate(normalizedRow, { abortEarly: false, convert: true });
    const trimmedRow = (validationResult.value ?? normalizedRow) as Partial<LeadCsvRow>;

    const lead: ICreateLeadRequest = {
        name: trimmedRow.name || '',
        email: trimmedRow.email || null,
        phone_number: trimmedRow.phone_number,
        ingestion_source: trimmedRow.ingestion_source || '',
        property: trimmedRow.property,
    };

    const errors = validationResult.error?.details
        .map((o) => o.context?.key as string)
        .filter(Boolean);

    const errorMessages = validationResult.error?.details
        .map((o) => o.message)
        .filter(Boolean);

    return {
        errors,
        errorMessages,
        lead,
    };
}

export function parseLeads(parsedCsv: ParsedCsv<LeadCsvRow>) {
    return parsedCsv.body.map(parseLead);
}

export const getUnknownLeadFields = (parsedCsv: ParsedCsv<LeadCsvRow>) => {
    const unknownFields: string[] = [];

    parsedCsv.header.forEach((header) => {
        const headerText = String(header ?? '').trim();
        if (!headerText) {
            return;
        }
        if (!importLeadsFields.includes(headerText)) {
            unknownFields.push(headerText);
        }
    });

    return unknownFields;
};
